import CheckListItem from "@/domain/model/checklist/item/CheckListItem.js";
import CheckListItemOption from "@/domain/model/checklist/item/option/CheckListItemOption.js";
import CheckListItemOptionMap from "@/domain/model/checklist/item/option/CheckListItemOptionMap.js";

/**
 * A checklist as it stood when an inspection was answered against it: the frozen copy
 * the server keeps on a CheckListVersion. It hands back ordinary CheckListItem and
 * CheckListItemOption objects built from the copy, so the screens that draw a live
 * checklist can draw the frozen one unchanged - but nothing in them reaches back to
 * the live records, which may since have been renamed, re-ordered or removed.
 *
 * The item and option ids are the live record ids, so they match the ids the answers
 * point at.
 */
export default class CheckListSnapshot {

  // The newest copy layout this client can read. A newer one is left unread and the
  // caller falls back to the live checklist, which is what it showed before copies.
  static SUPPORTED_FORMAT = 1;

  // Parsing the same copy for every row of every redraw is wasted work, and a copy
  // never changes once written, so the parsed form is kept against its text.
  static _cache = new Map();
  static _CACHE_LIMIT = 200;

  constructor(domain, data) {
    this.domain = domain;
    this.data = data;
  }

  /**
   * The copy in the given text, or null when there is none, it does not parse, or it
   * is in a layout this client does not know.
   */
  static parse(domain, text) {
    if (typeof text !== 'string' || text.length === 0) {
      return null;
    }
    let data = CheckListSnapshot._cache.get(text);
    if (data === undefined) {
      data = CheckListSnapshot._read(text);
      if (CheckListSnapshot._cache.size >= CheckListSnapshot._CACHE_LIMIT) {
        CheckListSnapshot._cache.clear();
      }
      CheckListSnapshot._cache.set(text, data);
    }
    if (data == null) {
      return null;
    }
    return new CheckListSnapshot(domain, data);
  }

  static _read(text) {
    let data = null;
    try {
      data = JSON.parse(text);
    } catch (e) {
      return null;
    }
    if (data == null || typeof data !== 'object' || Array.isArray(data)) {
      return null;
    }
    const format = data.format;
    if (typeof format !== 'number' || format < 1 || format > CheckListSnapshot.SUPPORTED_FORMAT) {
      return null;
    }
    if (!Array.isArray(data.items)) {
      return null;
    }
    return data;
  }

  format() {
    return this.data.format;
  }

  name() {
    return CheckListSnapshot._string(this.data.name);
  }

  description() {
    return CheckListSnapshot._string(this.data.description);
  }

  hasDescription() {
    return this.description().length > 0;
  }

  /**
   * The questions and information blocks in the order the copy holds them, which the
   * server writes sorted by index and then id.
   */
  items() {
    return this.data.items
      .filter(entry => entry != null && typeof entry === 'object')
      .map(entry => CheckListSnapshot.toItem(this.domain, entry));
  }

  /** The item with the given live id, or null when the copy has no such item. */
  item(itemId) {
    const entry = this._itemEntry(itemId);
    return entry ? CheckListSnapshot.toItem(this.domain, entry) : null;
  }

  hasItem(itemId) {
    return this._itemEntry(itemId) != null;
  }

  /** The item's choices in the copy's order, or an empty list for an unknown item. */
  options(itemId) {
    const entry = this._itemEntry(itemId);
    if (!entry) {
      return [];
    }
    return CheckListSnapshot._optionEntries(entry)
      .map(option => CheckListSnapshot.toOption(this.domain, option));
  }

  /** The choice with the given live id on the given item, or null. */
  option(itemId, optionId) {
    const entry = this._itemEntry(itemId);
    if (!entry) {
      return null;
    }
    const found = CheckListSnapshot._optionEntries(entry).find(option => option.id === optionId);
    return found ? CheckListSnapshot.toOption(this.domain, found) : null;
  }

  _itemEntry(itemId) {
    return this.data.items.find(entry => entry != null && entry.id === itemId) || null;
  }

  static _optionEntries(entry) {
    if (!Array.isArray(entry.options)) {
      return [];
    }
    return entry.options.filter(option => option != null && typeof option === 'object');
  }

  static _string(value) {
    if (value == null) {
      return "";
    }
    return String(value);
  }

  /** A CheckListItem carrying the copy's fields, with its choices attached. */
  static toItem(domain, entry) {
    const options = new CheckListItemOptionMap(domain, {});
    CheckListSnapshot._optionEntries(entry).forEach(option => {
      options.add(CheckListSnapshot.toOption(domain, option));
    });
    const data = {
      '@rid': entry.id,
      index: entry.index,
      name: entry.name,
      type: entry.type,
      content: entry.content,
      multiSelect: entry.multiSelect,
      warnOnChange: entry.warnOnChange,
      warnChangeThreshold: entry.warnChangeThreshold,
    };
    const item = new CheckListItem(domain, data);
    item.withOptions(options);
    return item;
  }

  /** A CheckListItemOption carrying the copy's name, place and severity. */
  static toOption(domain, entry) {
    return new CheckListItemOption(domain, {
      '@rid': entry.id,
      index: entry.index,
      name: entry.name,
      category: entry.category,
    });
  }
}
